import { ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { Select, SelectItem } from "@carbon/react";

type CarbonSelectProps = ComponentPropsWithoutRef<typeof Select>;

export interface SelectFieldOption {
  value: string;
  /** Already translated by the caller — same convention as `label` below. */
  label: string;
}

export type SelectFieldProps<TFieldValues extends FieldValues> = Omit<
  CarbonSelectProps,
  | "id"
  | "labelText"
  | "invalid"
  | "invalidText"
  | "name"
  | "onChange"
  | "onBlur"
  | "value"
  | "defaultValue"
  | "ref"
  | "children"
> & {
  /** The RHF field name — also becomes the select's `id`. */
  name: Path<TFieldValues>;
  /** The field's visible label. */
  label: string;
  control: Control<TFieldValues>;
  options: SelectFieldOption[];
  /** Rendered as a blank leading option (`value=""`) so "nothing chosen
   * yet" is a real, selectable value a Yup `.required()` can fail on —
   * without it a native `<select>` defaults to the first real option and
   * a required check could never fire. Omit it only when every value in
   * `options` is already a legitimate default. */
  placeholder?: string;
  /** Appends the translated `form.optional` marker ("(optional)") to `label`. Same convention
   * and same rationale as `TextField`'s `optional` — see that component's docstring for
   * Carbon's full required/optional-marking guidance. */
  optional?: boolean;
};

/**
 * A Carbon `Select` bound to a React Hook Form field via `Controller`,
 * the `Select` counterpart to `TextField`. Same contract: `invalid`/
 * `invalidText` come straight from `fieldState.error`, every other
 * `Select` prop passes through, and the label/id pairing makes the field
 * reachable via `getByLabelText`/`getByRole("combobox", { name: label })`.
 *
 * Options render through Carbon's own `SelectItem` (a plain `<option>`
 * under the hood) rather than the consumer building `<option>` tags
 * itself.
 *
 * Carbon associates a `Select`'s error differently from `TextInput`'s —
 * `aria-describedby` rather than `aria-errormessage` — but the same
 * guarantee holds: the id it carries resolves to the element rendering
 * `invalidText`, so a screen reader announces the real error here too.
 */
function SelectField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  options,
  placeholder,
  optional,
  ...rest
}: SelectFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const labelText = optional ? `${label} ${t("form.optional")}` : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Select
          {...rest}
          {...field}
          id={name}
          labelText={labelText}
          invalid={Boolean(fieldState.error)}
          invalidText={fieldState.error?.message}
        >
          {placeholder ? <SelectItem value="" text={placeholder} /> : null}
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              text={option.label}
            />
          ))}
        </Select>
      )}
    />
  );
}

export default SelectField;
